import { atom } from "jotai";
import { atomFamily, loadable } from "jotai/utils";
import * as z from "zod";

import { Table } from "../types";
import { extractErrorMessageFromResponse } from "../utils";
import { getSubpathBridgeClientFetch } from "@zapier/identity/client/fetch";
import { listTablesResponseSchema } from "./schemas/tablesApiSchemas";

const tablesClient = getSubpathBridgeClientFetch("tables");

/* ------------------------------------------------------------------ */
/* SUSPENDING atom family                                             */
/* ------------------------------------------------------------------ */

export const tablesSearchAtomFamily_SUSPENDS = atomFamily(
  (searchTerm: string) => {
    const baseAtom = atom<Promise<Table[]>>(async () => {
      try {
        const searchParams = new URLSearchParams();
        if (searchTerm) {
          searchParams.append("q", searchTerm);
        }

        const res: unknown = await tablesClient(
          `/v1/tables?${searchParams.toString()}`,
        );

        if (!(res instanceof Response)) {
          console.error(`Unknown response type from bridge client for tables`);
          return [];
        }

        if (res.ok) {
          const json: unknown = await res.json();
          return listTablesResponseSchema.parse(json).data;
        } else {
          /* ----- 5xx or other unexpected errors – log & degrade ----- */
          const errorMessage = await extractErrorMessageFromResponse(res);
          console.error(
            `Failed to fetch tables${
              searchTerm ? ` with "${searchTerm}" searchTerm` : ""
            }: ${errorMessage}`,
          );
          return [];
        }
      } catch (error) {
        if (error instanceof z.ZodError) {
          const prettyError = z.prettifyError(error);
          console.error(`Tables API response validation failed:`, prettyError);
        } else {
          console.error(
            `Error fetching tables${
              searchTerm ? ` with "${searchTerm}" searchTerm` : ""
            }:`,
            error,
          );
        }

        return [];
      }
    });

    baseAtom.debugLabel = `tablesSearchAtomFamily_SUSPENDS(searchTerm:${searchTerm})`;
    return baseAtom;
  },
);

/* ------------------------------------------------------------------ */
/* NON-suspending (loadable) atom family                              */
/* ------------------------------------------------------------------ */

export const tablesSearchAtomFamily = atomFamily((searchTerm: string) => {
  const base = tablesSearchAtomFamily_SUSPENDS(searchTerm);
  return loadable(base);
});
